/**
 * Single source of truth for route paths. Builders keep params typed so a renamed
 * route cannot silently break a link.
 */
export const ROUTES = {
  root: '/',
  login: '/login',
  unauthorized: '/unauthorized',

  dashboard: '/dashboard',
  myTasks: '/my-tasks',
  projectTasks: (projectId: string): string => `/p/${encodeURIComponent(projectId)}/tasks`,

  profile: '/profile',
  settings: '/settings',
  /** What the user follows. The daily follow email links here. */
  notificationSettings: '/settings/notifications',

  families: '/families',
  familyDetail: (familyId: number): string => `/families/${String(familyId)}`,
  /** Family Library opened on project families, narrowed to one family name. */
  projectFamilies: (familyName: string): string => {
    const params = new URLSearchParams({ source: 'Project', q: familyName });
    return `/families?${params.toString()}`;
  },
  projectFamilyDetail: (familyId: number): string => `/families/project/${String(familyId)}`,
  tags: '/families/tags',

  /** The Core project workspace: projects that own members and documents (`/api/v1`). */
  workspace: '/p',
  projectHome: (projectId: string): string => `/p/${encodeURIComponent(projectId)}`,
  projectDocuments: (projectId: string): string => `/p/${encodeURIComponent(projectId)}/documents`,
  projectModels: (projectId: string): string => `/p/${encodeURIComponent(projectId)}/models`,
  /** A linked model opened inside its project; the same page as `projectDetail`, with the project around it. */
  projectModel: (projectId: string, modelId: number): string =>
    `/p/${encodeURIComponent(projectId)}/models/${String(modelId)}`,
  /**
   * Pages opened from inside a model keep the project and the model's tabs around them, so
   * moving between a model's sections never drops the navigation. Each reuses the param names
   * of its global route (`diffId`, `auditId`, `instanceId`, `familyId`, `modelId`).
   */
  projectModelFamily: (projectId: string, modelId: number, familyId: number): string =>
    `/p/${encodeURIComponent(projectId)}/models/${String(modelId)}/families/${String(familyId)}`,
  projectModelCompare: (projectId: string, modelId: number): string =>
    `/p/${encodeURIComponent(projectId)}/models/${String(modelId)}/timeline/compare`,
  projectModelDiff: (projectId: string, modelId: number, diffId: number): string =>
    `/p/${encodeURIComponent(projectId)}/models/${String(modelId)}/timeline/diffs/${String(diffId)}`,
  projectModelAudit: (projectId: string, modelId: number, auditId: number): string =>
    `/p/${encodeURIComponent(projectId)}/models/${String(modelId)}/audits/${String(auditId)}`,
  projectModelAuditRun: (projectId: string, modelId: number, runId: number): string =>
    `/p/${encodeURIComponent(projectId)}/models/${String(modelId)}/audits/runs/${String(runId)}`,
  projectModelChainRun: (projectId: string, modelId: number, chainRunId: number): string =>
    `/p/${encodeURIComponent(projectId)}/models/${String(modelId)}/audits/chain-runs/${String(chainRunId)}`,
  projectHealth: (projectId: string): string => `/p/${encodeURIComponent(projectId)}/health`,
  projectRequirements: (projectId: string): string => `/p/${encodeURIComponent(projectId)}/requirements`,
  projectAlbumReleases: (projectId: string): string => `/p/${encodeURIComponent(projectId)}/album-releases`,
  albumRelease: (projectId: string, releaseId: string): string =>
    `/p/${encodeURIComponent(projectId)}/album-releases/${encodeURIComponent(releaseId)}`,
  projectSheets: (projectId: string): string => `/p/${encodeURIComponent(projectId)}/sheets`,
  projectManifest: (projectId: string): string => `/p/${encodeURIComponent(projectId)}/manifest`,
  projectMembers: (projectId: string): string => `/p/${encodeURIComponent(projectId)}/members`,
  /** Project admin tools: linking models. */
  projectSettings: (projectId: string): string => `/p/${encodeURIComponent(projectId)}/settings`,
  documentDetail: (projectId: string, documentId: string): string =>
    `/p/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(documentId)}`,

  projects: '/projects',
  projectDetail: (projectId: number): string => `/projects/${String(projectId)}`,
  /** The project's Instances tab, with one placed instance pre-selected in the tree. */
  projectInstance: (projectId: number, instanceUniqueId: string): string =>
    `/projects/${String(projectId)}?tab=instances&instance=${encodeURIComponent(instanceUniqueId)}`,

  audits: '/audits',
  createAudit: '/audits/new',
  /** Runs of the audits that check the families library. */
  auditLibraryRuns: '/audits/library-runs',
  auditDetail: (auditId: number): string => `/audits/${String(auditId)}`,
  auditResults: (instanceId: number): string => `/audits/instances/${String(instanceId)}`,
  auditChainRun: (modelId: number, chainRunId: number): string =>
    `/audits/chain-runs/${String(modelId)}/${String(chainRunId)}`,

  /** Merge Model Requests for one model: `?modelId=<guid>`. */
  mergeRequests: '/merge-requests',
  /** Promote the current SHAR of a model to PROD: `?modelId=<guid>`. */
  mergeRequestNewProduction: '/merge-requests/new-production',
  mergeRequestDetail: (requestId: string): string => `/merge-requests/${encodeURIComponent(requestId)}`,

  snapshots: '/snapshots',
  snapshotsForModel: (modelId: number): string => `/snapshots/model/${String(modelId)}`,
  compareSnapshots: '/snapshots/compare',
  diffDetail: (diffId: number): string => `/snapshots/diffs/${String(diffId)}`,
} as const;

/** Route patterns used when registering routes with the router. */
export const ROUTE_PATTERNS = {
  familyDetail: '/families/:familyId',
  projectFamilyDetail: '/families/project/:familyId',
  projectDetail: '/projects/:projectId',
  projectHome: '/p/:projectId',
  projectDocuments: '/p/:projectId/documents',
  projectModels: '/p/:projectId/models',
  projectModel: '/p/:projectId/models/:modelId',
  projectModelFamily: '/p/:projectId/models/:modelId/families/:familyId',
  projectModelCompare: '/p/:projectId/models/:modelId/timeline/compare',
  projectModelDiff: '/p/:projectId/models/:modelId/timeline/diffs/:diffId',
  projectModelAudit: '/p/:projectId/models/:modelId/audits/:auditId',
  projectModelAuditRun: '/p/:projectId/models/:modelId/audits/runs/:instanceId',
  projectModelChainRun: '/p/:projectId/models/:modelId/audits/chain-runs/:chainRunId',
  projectHealth: '/p/:projectId/health',
  projectRequirements: '/p/:projectId/requirements',
  projectAlbumReleases: '/p/:projectId/album-releases',
  albumRelease: '/p/:projectId/album-releases/:releaseId',
  projectSheets: '/p/:projectId/sheets',
  projectManifest: '/p/:projectId/manifest',
  projectMembers: '/p/:projectId/members',
  projectSettings: '/p/:projectId/settings',
  documentDetail: '/p/:projectId/documents/:documentId',
  auditDetail: '/audits/:auditId',
  auditResults: '/audits/instances/:instanceId',
  auditLibraryRuns: '/audits/library-runs',
  auditChainRun: '/audits/chain-runs/:modelId/:chainRunId',
  snapshotsForModel: '/snapshots/model/:modelId',
  diffDetail: '/snapshots/diffs/:diffId',
  mergeRequestDetail: '/merge-requests/:requestId',
} as const;
